import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will process the session_id and set the cookie.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    // We try /auth/me even without a JWT — a session_token cookie may authorize us.
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      // Nothing to keep: drop any stale JWT
      localStorage.removeItem("jl_token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("jl_token", data.access_token);
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("jl_token", data.access_token);
    setUser(data.user);
    return data.user;
  };

  /**
   * Exchange an Emergent session_id (URL fragment) for a persistent session cookie.
   * If referral_code was captured before the OAuth redirect, forward it so brand-new
   * Google-created accounts inherit their referrer.
   */
  const googleExchange = async (session_id, referral_code) => {
    const { data } = await api.post("/auth/google/session", {
      session_id,
      referral_code: referral_code || undefined,
    });
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("jl_token");
    setUser(null);
  };

  const updateProfile = async (payload) => {
    const { data } = await api.patch("/auth/profile", payload);
    setUser(data);
    return data;
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, login, register, googleExchange, logout, refresh, updateProfile }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
